import produce from 'immer';
import { LDFlagSet } from 'launchdarkly-js-sdk-common';
import { getRewardsFlag } from './helpers';
import { urlCeHome } from '../../server/utils/routeConstants';
import enableSale from './enableSaleConfig';

export type UserType = {
  id: string;
  firstName: string;
  accountRole?: {
    accountRoleTitle: string;
  };
  permissions?: {
    [key: string]: any;
  };
};

export enum MenuItemDecorationEnum {
  HEADER,
  BUTTON,
  CHECKBOX,
}

export type MenuItemType = {
  text: string;
  type?: MenuItemDecorationEnum;
  link?: string;
  /* eslint-disable no-use-before-define */
  menuItems?: MenuItemType[];
  className?: string;
};

const accountMenuItems = [
  { text: 'Profile', link: '/account', type: MenuItemDecorationEnum.BUTTON },
  {
    text: 'Subscriptions',
    link: '/account/subscriptions',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Orders',
    link: '/account/orders',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Addresses',
    link: '/account/addresses',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Payments',
    link: '/account/payments',
    type: MenuItemDecorationEnum.BUTTON,
  },
  { text: 'Log out', link: 'signout', type: MenuItemDecorationEnum.BUTTON },
];

const accountMenuItemsWithRewards = [
  { text: 'Profile', link: '/account', type: MenuItemDecorationEnum.BUTTON },
  {
    text: 'Rewards',
    link: '/account/rewards',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Subscriptions',
    link: '/account/subscriptions',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Orders',
    link: '/account/orders',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Addresses',
    link: '/account/addresses',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Payments',
    link: '/account/payments',
    type: MenuItemDecorationEnum.BUTTON,
  },
  { text: 'Log out', link: 'signout', type: MenuItemDecorationEnum.BUTTON },
];

type CreateAccountMenuConfigParam = {
  isLoggedIn: boolean;
  user?: UserType;
  flags: LDFlagSet;
};

export function createAccountMenuConfig({
  isLoggedIn,
  user,
  flags,
}: CreateAccountMenuConfigParam) {
  if (!isLoggedIn) return [];
  const rewardPoints = getRewardsFlag(flags);
  const firstName = user && user.firstName;

  // write these out like this because default
  // value destructure didn't seem to work / lp
  const adminAccess =
    user &&
    user.permissions &&
    user.permissions.oneCommerce &&
    user.permissions.oneCommerce.admin &&
    user.permissions.oneCommerce.admin.access;

  const ceoAccess =
    user &&
    user.permissions &&
    user.permissions.oneCommerce &&
    user.permissions.oneCommerce.ceo &&
    user.permissions.oneCommerce.ceo.access;

  const isAdmin = !!adminAccess;
  const isCE = !!ceoAccess;

  const accountRoleTitle =
    (user && user.accountRole && user.accountRole.accountRoleTitle) ||
    'Customer';

  const myAccountObject = {
    text: `Hi, ${firstName}`,
    subText: accountRoleTitle,
    type: MenuItemDecorationEnum.HEADER,
    menuItems: !rewardPoints
      ? [...accountMenuItems]
      : [...accountMenuItemsWithRewards],
  };

  const baseAccountObject = produce(myAccountObject, draft => {
    if (isCE) {
      draft.menuItems.unshift({
        text: 'CE Back Office',
        link: 'https://ce.onehopewine.com',
        type: MenuItemDecorationEnum.BUTTON,
      });
    }
    if (isAdmin) {
      draft.menuItems.unshift({
        text: 'Admin Dashboard',
        link: '/admin',
        type: MenuItemDecorationEnum.BUTTON,
      });
    }
  });
  return [baseAccountObject];
}

const saleMenuItem: MenuItemType = {
  text: 'Sale',
  link: '/sale',
  type: MenuItemDecorationEnum.BUTTON,
};

const shopMenuItems: MenuItemType[] = [
  {
    text: 'Shop all',
    link: '/',
    type: MenuItemDecorationEnum.BUTTON,
  },
  // {
  //   text: 'Sale',
  //   link: '/sale',
  //   type: MenuItemDecorationEnum.BUTTON,
  // },
  {
    text: 'Red Wine',
    link: '/wine/wine-red',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'White Wine',
    link: '/wine/wine-white',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Rosé Wine',
    link: '/wine/wine-rose',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Sparkling Wine',
    link: '/wine/wine-sparkling',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Etched Wine',
    link: '/wine/wine-etched',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Best Sellers',
    link: '/wine/wine-best-selling',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Award Winning',
    link: '/wine/wine-collection-award-winning',
    type: MenuItemDecorationEnum.BUTTON,
  },
  // {
  //   text: 'New Arrivals',
  //   link: '/wine-new-arrivals',
  //   type: MenuItemDecorationEnum.BUTTON,
  // },
  {
    text: 'Curated Mixed Packs',
    link: '/wine/mixed-packs',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Gift Boxes',
    link: '/gifts',
    type: MenuItemDecorationEnum.BUTTON,
  },
  {
    text: 'Swag',
    link: '/swag',
    type: MenuItemDecorationEnum.BUTTON,
  },
];

const createShopMenuItems = (
  items: MenuItemType[] = shopMenuItems,
): MenuItemType[] => {
  const updatedItems = ([] as MenuItemType[]).concat(items);
  if (enableSale) {
    updatedItems.splice(1, 0, saleMenuItem);
  }
  return updatedItems;
};

export const q32020Config = (isProspect: boolean): MenuItemType[] => {
  const baseConfig = [
    {
      text: 'Shop',
      type: MenuItemDecorationEnum.HEADER,
      menuItems: createShopMenuItems(),
    },
    {
      text: 'Virtual Tastings',
      type: MenuItemDecorationEnum.HEADER,
      menuItems: [
        // {
        //   text: 'December 2',
        //   link: '/wine/virtual/december-2',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'December 9',
        //   link: '/wine/virtual/december-9',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'December 16',
        //   link: '/wine/virtual/december-16',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'December 23',
        //   link: '/wine/virtual/december-23',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'December 30',
        //   link: '/wine/virtual/december-30',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'January 6',
        //   link: '/wine/virtual/january-6',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'January 13',
        //   link: '/wine/virtual/january-13',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'January 20',
        //   link: '/wine/virtual/january-20',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'January 27',
        //   link: '/wine/virtual/january-27',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'February 3',
        //   link: '/wine/virtual/february-3',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'February 10',
        //   link: '/wine/virtual/february-10',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'February 17',
        //   link: '/wine/virtual/february-17',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'February 24',
        //   link: '/wine/virtual/february-24',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'March 3',
        //   link: '/wine/virtual/march-3',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'March 10',
        //   link: '/wine/virtual/march-10',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'March 17',
        //   link: '/wine/virtual/march-17',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'March 24',
        //   link: '/wine/virtual/march-24',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'March 31',
        //   link: '/wine/virtual/march-31',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'April 7',
        //   link: '/wine/virtual/april-7',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'April 14',
        //   link: '/wine/virtual/april-14',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'April 21',
        //   link: '/wine/virtual/april-21',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'April 28',
        //   link: '/wine/virtual/april-28',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'May 5',
        //   link: '/wine/virtual/may-5',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'May 12',
        //   link: '/wine/virtual/may-12',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'May 19',
        //   link: '/wine/virtual/may-19',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        // {
        //   text: 'May 26',
        //   link: '/wine/virtual/may-26',
        //   type: MenuItemDecorationEnum.BUTTON,
        // },
        {
          text: 'June 2',
          link: '/wine/virtual/june-2',
          type: MenuItemDecorationEnum.BUTTON,
        },
        {
          text: 'June 9',
          link: '/wine/virtual/june-9',
          type: MenuItemDecorationEnum.BUTTON,
        },
        {
          text: 'June 16',
          link: '/wine/virtual/june-16',
          type: MenuItemDecorationEnum.BUTTON,
        },
        {
          text: 'June 23',
          link: '/wine/virtual/june-23',
          type: MenuItemDecorationEnum.BUTTON,
        },
        {
          text: 'June 30',
          link: '/wine/virtual/june-30',
          type: MenuItemDecorationEnum.BUTTON,
        },
      ],
    },
    {
      text: 'Perks',
      link: '/preferred',
      type: MenuItemDecorationEnum.HEADER,
    },
    {
      text: 'Visit',
      link: '/visit',
      type: MenuItemDecorationEnum.HEADER,
    },
    { text: 'Host', link: '/host', type: MenuItemDecorationEnum.HEADER },
    { text: 'Join', link: '/join', type: MenuItemDecorationEnum.HEADER },
    {
      text: 'About',
      type: MenuItemDecorationEnum.HEADER,
      menuItems: [
        {
          text: 'Our Story',
          link: '/about-us',
          type: MenuItemDecorationEnum.BUTTON,
        },
        {
          text: 'Our Impact',
          link: '/impact',
          type: MenuItemDecorationEnum.BUTTON,
        },
        {
          text: 'Our Winemaking',
          link: '/winemaking',
          type: MenuItemDecorationEnum.BUTTON,
        },
      ],
    },
    {
      text: 'Blog',
      link: 'https://www.onehopewine.com/blog',
      type: MenuItemDecorationEnum.HEADER,
    },
  ] as MenuItemType[];

  if (isProspect) {
    const tastingFlightsMenuItem: MenuItemType = {
      text: 'Tasting Flights',
      link: '/tasting-flight',
      type: MenuItemDecorationEnum.HEADER,
    };
    baseConfig.splice(1, 0, tastingFlightsMenuItem);
  }

  return baseConfig;
};

export const q12021Config: MenuItemType[] = [
  {
    text: 'Shop',
    type: MenuItemDecorationEnum.HEADER,
    menuItems: createShopMenuItems(),
  },
  {
    text: 'Virtual Tastings',
    type: MenuItemDecorationEnum.HEADER,
    menuItems: [
      // {
      //   text: 'December 2',
      //   link: '/wine/virtual/december-2',
      //   type: MenuItemDecorationEnum.BUTTON,
      // },
      // {
      //   text: 'December 9',
      //   link: '/wine/virtual/december-9',
      //   type: MenuItemDecorationEnum.BUTTON,
      // },
      // {
      //   text: 'December 16',
      //   link: '/wine/virtual/december-16',
      //   type: MenuItemDecorationEnum.BUTTON,
      // },
      // {
      //   text: 'December 23',
      //   link: '/wine/virtual/december-23',
      //   type: MenuItemDecorationEnum.BUTTON,
      // },
      // {
      //   text: 'December 30',
      //   link: '/wine/virtual/december-30',
      //   type: MenuItemDecorationEnum.BUTTON,
      // },
      // {
      //   text: 'January 6',
      //   link: '/wine/virtual/january-6',
      //   type: MenuItemDecorationEnum.BUTTON,
      // },
      // {
      //   text: 'January 13',
      //   link: '/wine/virtual/january-13',
      //   type: MenuItemDecorationEnum.BUTTON,
      // },
      // {
      //   text: 'January 20',
      //   link: '/wine/virtual/january-20',
      //   type: MenuItemDecorationEnum.BUTTON,
      // },
      // {
      //   text: 'January 27',
      //   link: '/wine/virtual/january-27',
      //   type: MenuItemDecorationEnum.BUTTON,
      // },
      // {
      //   text: 'February 3',
      //   link: '/wine/virtual/february-3',
      //   type: MenuItemDecorationEnum.BUTTON,
      // },
      // {
      //   text: 'February 10',
      //   link: '/wine/virtual/february-10',
      //   type: MenuItemDecorationEnum.BUTTON,
      // },
      // {
      //   text: 'February 17',
      //   link: '/wine/virtual/february-17',
      //   type: MenuItemDecorationEnum.BUTTON,
      // },
      {
        text: 'February 24',
        link: '/wine/virtual/february-24',
        type: MenuItemDecorationEnum.BUTTON,
      },
    ],
  },
  {
    text: 'Visit',
    link: '/visit',
    type: MenuItemDecorationEnum.HEADER,
  },
  { text: 'Host', link: '/host', type: MenuItemDecorationEnum.HEADER },
  { text: 'Join', link: '/join', type: MenuItemDecorationEnum.HEADER },
  {
    text: 'About',
    type: MenuItemDecorationEnum.HEADER,
    menuItems: [
      {
        text: 'Our Story',
        link: '/about-us',
        type: MenuItemDecorationEnum.BUTTON,
      },
      {
        text: 'Our Impact',
        link: '/impact',
        type: MenuItemDecorationEnum.BUTTON,
      },
      {
        text: 'Our Winemaking',
        link: '/winemaking',
        type: MenuItemDecorationEnum.BUTTON,
      },
    ],
  },
  {
    text: 'Blog',
    link: 'https://www.onehopewine.com/blog',
    type: MenuItemDecorationEnum.HEADER,
  },
];
